import React, { useState, useCallback, useRef, useEffect } from 'react';
import '../styles/product-image-gallery.css';
import {
  MIN_ZOOM,
  MAX_ZOOM,
  DOUBLE_TAP_MS,
  DOUBLE_TAP_MAX_DIST,
  MIN_SWIPE_PX,
  getLightboxMaxWidth,
  THUMB_MAX_WIDTH,
  appendImageSizeParams,
  getTouchDistance,
  normalizeGalleryItems,
  useDragToScroll,
  scrollThumbIntoView,
} from './productGalleryShared';

/**
 * Галерея зображень товару для десктопу: стрілки вліво/вправо біля головного зображення та в lightbox,
 * мініатюри внизу, клавіатура в lightbox.
 */
const ProductImageGalleryDesktop = ({ items = [], showThumbnails = true }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const touchStartRef = useRef({ x: 0, y: 0 });
  const overlayRef = useRef(null);
  const lightboxThumbsRef = useRef(null);
  const inlineThumbsRef = useRef(null);
  const pinchStartRef = useRef(null);
  const panStartRef = useRef(null);
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });
  const inlineTouchStartRef = useRef({ x: 0, y: 0 });
  const inlineSwipeHandledRef = useRef(false);

  const normalizedItems = normalizeGalleryItems(items);
  const hasMultiple = normalizedItems.length > 1;
  const showStrip = showThumbnails && normalizedItems.length > 1;

  const inlineThumbsDidDragRef = useDragToScroll(inlineThumbsRef, showStrip);
  const lightboxThumbsDidDragRef = useDragToScroll(lightboxThumbsRef, lightboxOpen);

  const goTo = useCallback(
    (index) => {
      setCurrentIndex((prev) => {
        if (index < 0) return hasMultiple ? normalizedItems.length - 1 : 0;
        if (index >= normalizedItems.length) return hasMultiple ? 0 : prev;
        return index;
      });
    },
    [hasMultiple, normalizedItems.length]
  );

  const openLightbox = useCallback(() => {
    if (inlineSwipeHandledRef.current) {
      inlineSwipeHandledRef.current = false;
      return;
    }
    setLightboxOpen(true);
  }, []);

  const inlineTouchStart = useCallback(
    (e) => {
      if (!hasMultiple || !e.touches?.length) return;
      inlineTouchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      inlineSwipeHandledRef.current = false;
    },
    [hasMultiple]
  );

  const inlineTouchEnd = useCallback(
    (e) => {
      if (!hasMultiple || e.changedTouches?.length !== 1) return;
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const dx = endX - inlineTouchStartRef.current.x;
      const dy = endY - inlineTouchStartRef.current.y;
      if (Math.abs(dx) > MIN_SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
        goTo(dx > 0 ? currentIndex - 1 : currentIndex + 1);
        inlineSwipeHandledRef.current = true;
      }
    },
    [hasMultiple, goTo, currentIndex]
  );

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setZoomScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (lightboxOpen) {
      setZoomScale(1);
      setTranslate({ x: 0, y: 0 });
    }
  }, [currentIndex, lightboxOpen]);

  const handleTouchStart = (e) => {
    if (!lightboxOpen) return;
    const touches = e.touches;
    if (touches.length === 2) {
      pinchStartRef.current = {
        distance: getTouchDistance(touches),
        scale: zoomScale,
      };
      panStartRef.current = null;
    } else if (touches.length === 1) {
      touchStartRef.current = { x: touches[0].clientX, y: touches[0].clientY };
      if (zoomScale > 1) {
        panStartRef.current = {
          x: touches[0].clientX,
          y: touches[0].clientY,
          translateX: translate.x,
          translateY: translate.y,
        };
      } else {
        panStartRef.current = null;
      }
    }
  };

  const handleTouchMove = (e) => {
    if (!lightboxOpen) return;
    const touches = e.touches;
    if (touches.length === 2 && pinchStartRef.current) {
      e.preventDefault();
      const dist = getTouchDistance(touches);
      if (pinchStartRef.current.distance > 0) {
        const newScale = Math.min(
          MAX_ZOOM,
          Math.max(MIN_ZOOM, pinchStartRef.current.scale * (dist / pinchStartRef.current.distance))
        );
        setZoomScale(newScale);
        if (newScale <= 1) setTranslate({ x: 0, y: 0 });
      }
    } else if (touches.length === 1 && panStartRef.current && zoomScale > 1) {
      e.preventDefault();
      setTranslate({
        x: panStartRef.current.translateX + (touches[0].clientX - panStartRef.current.x),
        y: panStartRef.current.translateY + (touches[0].clientY - panStartRef.current.y),
      });
    }
  };

  const handleTouchEnd = (e) => {
    if (!lightboxOpen) return;
    if (e.touches.length === 2) return;
    if (e.touches.length === 0 && e.changedTouches.length === 1) {
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const dx = endX - touchStartRef.current.x;
      const dy = endY - touchStartRef.current.y;
      const dist = Math.hypot(dx, dy);

      if (dist < DOUBLE_TAP_MAX_DIST) {
        const now = Date.now();
        if (now - lastTapRef.current.time < DOUBLE_TAP_MS) {
          setZoomScale((s) => (s > 1 ? 1 : 2));
          setTranslate({ x: 0, y: 0 });
          lastTapRef.current = { time: 0, x: 0, y: 0 };
          pinchStartRef.current = null;
          panStartRef.current = null;
          return;
        }
        lastTapRef.current = { time: now, x: endX, y: endY };
      }

      if (zoomScale > 1) {
        pinchStartRef.current = null;
        panStartRef.current = null;
        return;
      }

      if (Math.abs(dy) > MIN_SWIPE_PX && dy > 0 && Math.abs(dy) > Math.abs(dx)) {
        closeLightbox();
        return;
      }
      if (hasMultiple && Math.abs(dx) > MIN_SWIPE_PX) {
        if (dx > 0) goTo(currentIndex - 1);
        else goTo(currentIndex + 1);
      }
    }
    if (e.touches.length === 1) {
      panStartRef.current =
        zoomScale > 1
          ? {
              x: e.touches[0].clientX,
              y: e.touches[0].clientY,
              translateX: translate.x,
              translateY: translate.y,
            }
          : null;
    }
  };

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (hasMultiple && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        goTo(currentIndex + (e.key === 'ArrowRight' ? 1 : -1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, closeLightbox, hasMultiple, currentIndex, goTo]);

  useEffect(() => {
    if (lightboxOpen) document.body.classList.add('product-gallery-lightbox-open');
    else document.body.classList.remove('product-gallery-lightbox-open');
    return () => document.body.classList.remove('product-gallery-lightbox-open');
  }, [lightboxOpen]);

  useEffect(() => {
    if (!showStrip) return;
    scrollThumbIntoView(inlineThumbsRef.current, currentIndex, { lightbox: false });
  }, [currentIndex, showStrip]);

  useEffect(() => {
    if (!lightboxOpen || !showStrip) return;
    const t = requestAnimationFrame(() => {
      scrollThumbIntoView(lightboxThumbsRef.current, currentIndex, { lightbox: true });
    });
    return () => cancelAnimationFrame(t);
  }, [lightboxOpen, currentIndex, showStrip]);

  /* Інлайн і lightbox: центрування обраної мініатюри при зміні currentIndex. */
  if (normalizedItems.length === 0) return null;

  const current = normalizedItems[currentIndex];

  return (
    <div className="product-image-gallery product-image-gallery--desktop">
      <div className="product-gallery-inline">
        <div className="product-gallery-inline-main">
          <div
            className="product-gallery-main-wrap"
            onClick={openLightbox}
            onTouchStart={inlineTouchStart}
            onTouchEnd={inlineTouchEnd}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && openLightbox()}
            aria-label="Відкрити повний екран"
          >
            <img src={current.original} alt="" className="product-gallery-main-img" />
            {hasMultiple && (
              <span className="product-gallery-expand-hint" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              </span>
            )}
          </div>
          {hasMultiple && (
            <>
              <button
                type="button"
                className="product-gallery-inline-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  goTo(currentIndex - 1);
                }}
                aria-label="Попереднє зображення"
              >
                <svg className="product-gallery-arrow-svg" viewBox="6 0 12 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                type="button"
                className="product-gallery-inline-next"
                onClick={(e) => {
                  e.stopPropagation();
                  goTo(currentIndex + 1);
                }}
                aria-label="Наступне зображення"
              >
                <svg className="product-gallery-arrow-svg" viewBox="6 0 12 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </>
          )}
        </div>
        {showStrip && (
          <div className="product-gallery-thumbnails product-gallery-thumbnails--draggable" ref={inlineThumbsRef}>
            {normalizedItems.map((item, i) => (
              <button
                key={i}
                type="button"
                className={`product-gallery-thumb ${i === currentIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (inlineThumbsDidDragRef.current) {
                    inlineThumbsDidDragRef.current = false;
                    return;
                  }
                  setCurrentIndex(i);
                }}
                aria-label={`Зображення ${i + 1}`}
              >
                <img src={appendImageSizeParams(item.thumbnail, { maxWidth: THUMB_MAX_WIDTH })} alt="" draggable={false} />
              </button>
            ))}
          </div>
        )}
      </div>

      {lightboxOpen && (
        <div
          ref={overlayRef}
          className="product-gallery-lightbox"
          onClick={(e) => e.target === overlayRef.current && closeLightbox()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          role="dialog"
          aria-modal="true"
          aria-label="Перегляд зображень"
        >
          <button type="button" className="product-gallery-lightbox-close" onClick={closeLightbox} aria-label="Закрити">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <div className="product-gallery-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <div
              className={`product-gallery-lightbox-image-wrap product-gallery-lightbox-zoom-wrap${
                zoomScale !== 1 || translate.x !== 0 || translate.y !== 0 ? ' product-gallery-lightbox-zoomed' : ''
              }`}
              style={
                zoomScale !== 1 || translate.x !== 0 || translate.y !== 0
                  ? { transform: `scale(${zoomScale}) translate(${translate.x}px, ${translate.y}px)` }
                  : {}
              }
            >
              <img
                src={appendImageSizeParams(current.original, { maxWidth: getLightboxMaxWidth() })}
                alt=""
                className="product-gallery-lightbox-img"
                draggable={false}
              />
            </div>
          </div>

          {hasMultiple && (
            <>
              <button
                type="button"
                className="product-gallery-lightbox-prev product-gallery-lightbox-arrow-desktop"
                onClick={() => goTo(currentIndex - 1)}
                aria-label="Попереднє"
              >
                <svg className="product-gallery-arrow-svg" viewBox="6 0 12 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                type="button"
                className="product-gallery-lightbox-next product-gallery-lightbox-arrow-desktop"
                onClick={() => goTo(currentIndex + 1)}
                aria-label="Наступне"
              >
                <svg className="product-gallery-arrow-svg" viewBox="6 0 12 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </>
          )}

          {showStrip && (
            <div className="product-gallery-lightbox-thumbnails product-gallery-thumbnails--draggable" ref={lightboxThumbsRef}>
              <div className="product-gallery-lightbox-thumbnails-inner">
                {normalizedItems.map((item, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`product-gallery-lightbox-thumb ${i === currentIndex ? 'active' : ''}`}
                    onClick={() => {
                      if (lightboxThumbsDidDragRef.current) {
                        lightboxThumbsDidDragRef.current = false;
                        return;
                      }
                      setCurrentIndex(i);
                    }}
                    aria-label={`Зображення ${i + 1}`}
                  >
                    <img src={appendImageSizeParams(item.thumbnail, { maxWidth: THUMB_MAX_WIDTH })} alt="" draggable={false} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductImageGalleryDesktop;
